import { FormProvider, useForm } from 'react-hook-form';
import { Alert, Button, IconAttention, media, styled } from '@payright/web-components';
import SCSpinner from '../../../components/spinner';
import { useState, useRef, ReactNode } from 'react';
import { ButtonCancelPlan } from '../../../components/buttons/ButtonCancelPlan';
import { ControlledCheckbox } from '../../../components/form-fields';
import { PdfBankStatementLogos } from './PdfBankStatementLogos';

// Import React FilePond
import { FilePond, registerPlugin, FileStatus } from 'react-filepond';
import type { FilePondFile } from 'filepond';

// Import FilePond plugins
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';

// Import FilePond styles
import 'filepond/dist/filepond.min.css';

// Register FilePond plugins to enable file validation
registerPlugin(FilePondPluginFileValidateType, FilePondPluginFileValidateSize);

export type PdfBankStatementsFormData = {
  fileToUpload: unknown[];
};

type PdfBankStatementsProps = {
  loading: boolean;
  customerNumber: string;
  onSubmit: (data: PdfBankStatementsFormData) => void;
  alert?: ReactNode;
};

const StyledForm = styled.form`
  display: grid;
  grid-gap: 2rem;

  .title {
    color: ${(props) => props.theme.colours.blue.base};
    font-weight: bold;
    font-size: 1.125rem;
  }

  ${media.min.medium} {
    .title {
      font-size: 1.5rem;
    }
  }

  .buttons {
    display: flex;
    gap: 1.5rem;
    flex-direction: column-reverse;
  }

  p {
    font-size: 1.13em;
  }

  ${media.min.small} {
    .buttons {
      flex-direction: row;
    }
  }
`;

const IllionLogo = styled.img`
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
`;

export const FormPdfBankStatements = ({ loading, alert, onSubmit }: PdfBankStatementsProps) => {
  const pondRef = useRef<FilePond | null>(null);

  const reactHookFormBsoData = useForm<{ agreedToTerms: boolean }>({
    defaultValues: {
      agreedToTerms: false,
    },
    mode: 'onSubmit',
  });
  const { handleSubmit, formState, setValue, watch } = reactHookFormBsoData;
  const { errors } = formState;

  const [clientSideError, setClientSideError] = useState<string | null>(null);

  // Watch the agreedToTerms value from ReactHookForm
  const agreedToTerms = watch('agreedToTerms');

  const handleAgreedToTermsClick = () => {
    setValue('agreedToTerms', !agreedToTerms, { shouldValidate: true });
  };

  const formSubmitHandler = async () => {
    // Clear any previous file validation errors
    setClientSideError(null);

    // Get all files from FilePond instance
    const allFiles: FilePondFile[] = pondRef.current?.getFiles() || [];

    if (allFiles.length === 0) {
      setClientSideError('Please upload at least one PDF bank statement file.');
      return;
    }

    const filesStillProcessing = allFiles.filter((fileItem: FilePondFile) =>
      [FileStatus.LOADING, FileStatus.PROCESSING, FileStatus.PROCESSING_QUEUED].includes(
        fileItem.status
      )
    );
    const validFiles = allFiles.filter(
      (fileItem: FilePondFile) => fileItem.status === FileStatus.PROCESSING_COMPLETE
    );

    // Check if any files are still processing
    if (filesStillProcessing.length > 0) {
      setClientSideError('Please wait for all files to finish processing before submitting.');
      return;
    }

    // Check if all files are valid
    if (validFiles.length !== allFiles.length) {
      setClientSideError(
        'All files must be valid before submitting. Please review the files highlighted in red.'
      );
      return;
    }

    // All files are valid, proceed with submission
    const actualFiles = validFiles.map((fileItem: FilePondFile) => fileItem.file);
    onSubmit({ fileToUpload: actualFiles });
  };

  return (
    <FormProvider {...reactHookFormBsoData}>
      <StyledForm onSubmit={handleSubmit(formSubmitHandler)}>
        <p className="title">Securely upload your PDF bank statements via our partner Experian.</p>
        <IllionLogo src="/images/logo-statement-ocr.png" alt="illion StatementOCR" />
        <PdfBankStatementLogos />
        <p>
          This service only processes PDF documents (official statements and their high-quality
          scanned copies). Other document types or file formats are not supported (e.g. screenshots
          or photos are not processed).
        </p>
        <FilePond
          ref={pondRef}
          allowMultiple={true}
          maxFiles={20}
          instantUpload={true}
          acceptedFileTypes={['application/pdf']}
          labelFileTypeNotAllowed="Only PDF files are allowed"
          maxFileSize="32MB"
          labelMaxFileSize="File size exceeds 32MB"
          labelMaxFileSizeExceeded="File is too large"
          maxTotalFileSize="64MB"
          labelMaxTotalFileSize="Total file size exceeds 64MB"
          labelMaxTotalFileSizeExceeded="Total size is too large"
          labelFileProcessing="Processing"
          labelFileProcessingComplete="Ready"
          server={{
            process: (_fieldName, file, _metadata, load, _errorCallback, _progress, _abort) => {
              // This marks the file as successfully processed i.e. "Ready"
              // It only reaches this part of the code if the file passes validation.
              load(file.name);
            },
            revert: null, // Prevent DELETE requests when files are removed from the list
          }}
          labelIdle="<span class='filepond--label-action'>Select files</span>"
          credits={false}
        />
        {clientSideError && (
          <Alert title="Error" body={clientSideError} outcome="error" icon={<IconAttention />} />
        )}
        {alert} {/* Renders alerts from redux slice */}
        <ControlledCheckbox
          name="agreedToTerms"
          rules={{
            validate: {
              test: (value) => {
                return !value ? '* Required' : undefined;
              },
            },
          }}
          checked={agreedToTerms}
          handleClick={handleAgreedToTermsClick}
          error={errors.agreedToTerms?.message}
        >
          <span>
            I agree to the{' '}
            <a
              href="https://www.bankstatements.com.au/about/terms"
              target="_blank"
              onClick={(e) => e.stopPropagation()}
            >
              End User Terms
            </a>{' '}
            &{' '}
            <a
              href="https://www.experian.com.au/privacy-policy-terms-conditions/risk-marketing-solutions-privacy-policy"
              target="_blank"
              onClick={(e) => e.stopPropagation()}
            >
              Privacy Policy
            </a>{' '}
            outlined by Experian.
          </span>
        </ControlledCheckbox>
        <div className="buttons">
          <ButtonCancelPlan disabled={loading} />
          <Button disabled={loading} maxWidth="100%" icon={loading ? <SCSpinner /> : undefined}>
            Submit
          </Button>
        </div>
      </StyledForm>
    </FormProvider>
  );
};
